import { useEffect, useRef, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ArrowClockwise, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  ChatExportDownloadError,
  requestChatExportDownload,
  type ChatExportDownloadFailure,
} from '@/services/chatExports';
import type { ChatFileBlockPayload } from '@/types/chatBlocks';
import { getFileTypeIcon } from '@/utils/fileTypeIcon';

export const CHAT_EXPORT_BUILD_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const;

type FileBlockState =
  | { phase: 'idle'; downloaded: boolean }
  | { phase: 'building'; downloaded: boolean }
  | { phase: 'error'; reason: ChatExportDownloadFailure };

const ERROR_COPY: Record<ChatExportDownloadFailure, string> = {
  building: 'Your file is still being built. Try again in a moment.',
  expired: 'That download link expired. Try again for a fresh one.',
  not_found: 'This file is no longer available.',
  rate_limited: 'Too many download attempts. Wait a minute and try again.',
  failed: 'We couldn’t prepare this file. Please try again.',
};

const waitForRetry = (delay: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const finish = () => {
      signal.removeEventListener('abort', abort);
      resolve();
    };

    const timer = window.setTimeout(finish, delay);

    const abort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', abort, { once: true });
  });

/** Open a signed URL without treating it as a permanent block payload. */
export const openChatExportDownload = async (url: string): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
    return;
  }

  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
};

const itemLabel = (payload: ChatFileBlockPayload): string | null => {
  if (payload.itemCount === undefined) return null;

  const source = payload.sourceLabel?.toLowerCase() ?? '';
  const singular = payload.itemCount === 1;

  const noun = source.includes('flashcard')
    ? singular
      ? 'card'
      : 'cards'
    : source.includes('answer')
      ? singular
        ? 'answer'
        : 'answers'
      : singular
        ? 'item'
        : 'items';

  return `${payload.itemCount} ${noun}`;
};

const FileBlock = ({ payload }: { payload: ChatFileBlockPayload }) => {
  const [state, setState] = useState<FileBlockState>(() =>
    payload.status === 'failed'
      ? { phase: 'error', reason: 'failed' }
      : { phase: 'idle', downloaded: false }
  );

  const requestRef = useRef<AbortController | null>(null);
  const FileIcon = getFileTypeIcon(payload.filename);
  const count = itemLabel(payload);
  const title = payload.title || payload.filename;
  const showFilename = title !== payload.filename;

  useEffect(
    () => () => {
      requestRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    requestRef.current?.abort();

    setState(
      payload.status === 'failed'
        ? { phase: 'error', reason: 'failed' }
        : { phase: 'idle', downloaded: false }
    );
  }, [payload.exportId, payload.status]);

  const download = async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setState({ phase: 'building', downloaded: false });

    for (let attempt = 0; ; attempt += 1) {
      try {
        const result = await requestChatExportDownload(
          payload.exportId,
          controller.signal
        );

        if (controller.signal.aborted) return;
        await openChatExportDownload(result.url);
        if (!controller.signal.aborted) {
          setState({ phase: 'idle', downloaded: true });
        }
        return;
      } catch (error) {
        if (controller.signal.aborted) return;

        const reason =
          error instanceof ChatExportDownloadError ? error.reason : 'failed';

        if (
          reason === 'building' &&
          attempt < CHAT_EXPORT_BUILD_RETRY_DELAYS_MS.length
        ) {
          try {
            await waitForRetry(
              CHAT_EXPORT_BUILD_RETRY_DELAYS_MS[attempt],
              controller.signal
            );

            continue;
          } catch {
            return;
          }
        }

        setState({ phase: 'error', reason });
        return;
      }
    }
  };

  const isBuilding = state.phase === 'building';

  return (
    <div className="flex min-w-0 flex-col gap-3 p-3 mobile:flex-row mobile:items-center">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-fg-secondary">
        <FileIcon size={22} weight="duotone" aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="m-0 break-words font-heading text-sm font-medium text-fg-primary">
          {title}
        </p>
        {showFilename ? (
          <p className="m-0 break-words text-xs text-fg-secondary">
            {payload.filename}
          </p>
        ) : null}
        <p className="m-0 text-xs text-fg-secondary">
          {[payload.format.toUpperCase(), count, payload.sourceLabel]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {payload.notes.map((note, index) => (
          <p
            className="m-0 mt-1 text-xs text-fg-secondary"
            key={`${index}-${note}`}
          >
            {note}
          </p>
        ))}
        {state.phase === 'error' ? (
          <p className="m-0 mt-1 text-xs text-destructive" role="alert">
            {ERROR_COPY[state.reason]}
          </p>
        ) : null}
      </div>

      <Button
        className="w-full mobile:w-auto"
        disabled={isBuilding}
        onClick={() => void download()}
        size="sm"
        type="button"
        variant="outline"
      >
        {isBuilding ? (
          <>
            <Spinner size={14} thickness={3} aria-hidden="true" />
            Building File…
          </>
        ) : state.phase === 'error' ? (
          <>
            <ArrowClockwise aria-hidden="true" />
            Try Again
          </>
        ) : (
          <>
            <DownloadSimple aria-hidden="true" />
            {state.downloaded ? 'Download Again' : 'Download'}
          </>
        )}
      </Button>
    </div>
  );
};

export default FileBlock;
